import api from '@/api';

import { PageResult } from '@/core/request/PageResult';

import {
  Customer,
  CreateCustomerRequest,
  UpdateCustomerRequest,
  CustomerLGPDActive,
  ListCustomersReducedQuery,
  CustomerReduced,
  CustomerGeneral,
  CustomerIndicatorRequest,
  CustomerRegisterInterface,
  CustomerIndicatorInterface,
  CustomerCpfCnpjExistsResponse,
  CustomerUpdateCategoryRequest,
  CustomerPortoIsValidData,
} from './Customers.types';

import { API_BASE_URL } from '..';
import {
  CustomerVehicle,
  CustomerVehicleReduced,
  CustomerVehiclesFilterParamsReduced,
  ListCustomerVehiclesFilterParams,
} from '../CustomerVehicles/CustomerVehicles.types';
import { removeCpfCnpjMask } from '@/utils/mask/removeMask';

const CONTROLLER_URL = `${API_BASE_URL}/customers`;

export async function listCustomersReduced(params?: ListCustomersReducedQuery) {
  const result = (await api.get(`${CONTROLLER_URL}/reduced`, {
    ...params,
    cpfCnpj: !!params?.cpfCnpj ? removeCpfCnpjMask(params?.cpfCnpj) : undefined,
  })) as unknown;

  return result as PageResult<CustomerReduced>;
}

export async function getCustomerGeneral(
  id?: number,
): Promise<CustomerGeneral> {
  const result = (await api.get(
    `${CONTROLLER_URL}/${id}/general`,
    null,
  )) as unknown;

  return result as CustomerGeneral;
}

export async function getCustomerReduced(
  id?: number,
): Promise<CustomerReduced> {
  const result = (await api.get(
    `${CONTROLLER_URL}/${id}/reduced`,
    null,
  )) as unknown;

  return result as CustomerReduced;
}

export async function createCustomer(
  obj: CreateCustomerRequest,
): Promise<Customer> {
  const result = (await api.post(`${CONTROLLER_URL}`, obj, null)) as unknown;

  return result as Customer;
}

export async function updateCustomer(
  customerId: number,
  obj: UpdateCustomerRequest,
): Promise<Customer> {
  const result = (await api.put(
    `${CONTROLLER_URL}/${customerId}`,
    obj,
    null,
  )) as unknown;

  return result as Customer;
}

export async function updateCustomerLGPD(params: CustomerLGPDActive) {
  const result = (await api.put(
    `${CONTROLLER_URL}/${params.id}/lgpdActive`,
    { enableLgpd: params.enableLgpd },
    null,
  )) as unknown;

  return result as void;
}

export async function updateCustomerShowLGPD(customerId: number) {
  const result = (await api.put(
    `${CONTROLLER_URL}/${customerId}/showLgpd`,
    {},
    null,
  )) as unknown;

  return result as void;
}

export async function deleteCustomer(id: number) {
  const result = (await api.remove(
    `${CONTROLLER_URL}/${id}`,
    {},
    null,
  )) as unknown;

  return result;
}

export async function activeLGPD(id: number) {
  const result = (await api.put(
    `${CONTROLLER_URL}/${id}/lgpdActive`,
    {},
    null,
  )) as unknown;

  return result;
}

export async function activeCustomer(id: number) {
  const result = (await api.put(
    `${CONTROLLER_URL}/${id}/active`,
    {},
    null,
  )) as unknown;

  return result;
}

export async function listVehiclesFilter(
  params?: ListCustomerVehiclesFilterParams,
) {
  const result = (await api.get(`${CONTROLLER_URL}/filter`, params)) as unknown;

  return result as PageResult<CustomerVehicle>;
}

export async function listVehiclesFilterReduced(
  params?: CustomerVehiclesFilterParamsReduced,
) {
  const result = (await api.get(
    `${CONTROLLER_URL}/filter/reduced`,
    params,
  )) as unknown;

  return result as PageResult<CustomerVehicleReduced>;
}

export async function getVehicleFilterReduced(vehicleId: number) {
  const result = (await api.get(
    `${CONTROLLER_URL}/filter/${vehicleId}/reduced`,
    {},
  )) as unknown;

  return result as CustomerVehicleReduced;
}

export async function customerIndicators() {
  return (await api.get(
    `${CONTROLLER_URL}/indicators`,
    {},
  )) as unknown as CustomerIndicatorInterface;
}

export async function customerIndicatorsGranularity(
  obj?: CustomerIndicatorRequest,
) {
  return (await api.get(
    `${CONTROLLER_URL}/indicators-granularity`,
    obj,
  )) as unknown as CustomerRegisterInterface[];
}

export async function customerCpfCnpjExists(cpfCnpj: string) {
  return (await api.get(
    `${CONTROLLER_URL}/cpf-cnpj/${cpfCnpj}/exists`,
    {},
  )) as unknown as CustomerCpfCnpjExistsResponse;
}

export async function customerUpdateCategory({
  id,
  ...payload
}: CustomerUpdateCategoryRequest) {
  return (await api.patch(
    `${CONTROLLER_URL}/${id}/categories`,
    payload,
    null,
  )) as unknown as CustomerCpfCnpjExistsResponse;
}

export async function customerPortoIsValid(customerId?: number) {
  return (await api.get(
    `${CONTROLLER_URL}/${customerId}/is-valid-porto-customer`,
    null,
  )) as unknown as CustomerPortoIsValidData;
}
